import * as React from 'react';
import { Link } from '@tanstack/react-router';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { FAQ_ENTRIES } from '@/routes/supplier/conditions/faq-content';

// =============================================================================
// faq-screen.tsx — Page « Foire aux questions » FOURNISSEUR (redesign GATE_HUMAN 2026-09-12).
// =============================================================================
// Redesign « repère graphique » : plus de carte englobante ni de cartes par question ; contenu posé
// DIRECTEMENT sur le fond en GRILLE 2 colonnes (sommaire sticky 300px à gauche + lecture fluide à droite,
// gap 48px, largeur totale 1180px centrée). Le CONTENU reste INCHANGÉ (source unique faq-content.ts,
// ancré DBU §1.4.1/§1.4.7/§3.1/§3.3/§3.5/§3.6). Contenu FIXE plateforme : aucune donnée serveur.
//
// Lecture : TOUTES les questions sont ouvertes (plus d'accordéon, plus de triangles). Chaque question est
// un <article> ancré par son id ; le sommaire de gauche est un <nav> de liens d'ancre avec scrollspy
// (IntersectionObserver) marquant l'entrée courante. Clic → défilement doux respectant prefers-reduced-motion.
//
// WCAG 2.1 AA : h1 étiquetant la <section>, articles à heading navigable (<h2>), sommaire
// (<nav aria-label>) dont l'entrée active porte aria-current="true", focus visible clavier (global),
// code couleur du sommaire DOUBLÉ par le poids (font-medium) et le trait — jamais couleur seule.
// Anti-LLMism éditorial : contenu sans em-dash de substitution.

/** « DBU §1.4.1, §3.5 » → « DBU §1.4.1 · §3.5 » (séparateur médian, non cliquable). */
function formatSource(source: string): string {
  return source.replace(/\s*,\s*/g, ' · ');
}

/**
 * Pliage de recherche : insensible à la casse ET aux accents (même logique que la recherche
 * catalogue et la liste /echanges). « Échéance » et « echeance » matchent la même requête.
 */
function foldSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/** Texte recherchable d'une entrée : shortLabel + question + réponse (match partiel). */
function matchesQuery(entry: (typeof FAQ_ENTRIES)[number], needle: string): boolean {
  if (needle === '') return true;
  const haystack = foldSearch(
    `${entry.shortLabel} ${entry.question} ${entry.answer}`,
  );
  return haystack.includes(needle);
}

/** Vrai si l'utilisateur a demandé une réduction des animations (guarde matchMedia absent en test). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Scrollspy : suit la section visible et renvoie son id (entrée active du sommaire).
 * Dégrade proprement là où IntersectionObserver est absent (jsdom) : l'entrée active
 * reste la première, l'UI ne dépend jamais de l'observer pour se monter.
 */
function useScrollSpy(
  ids: readonly string[],
): readonly [string, React.Dispatch<React.SetStateAction<string>>] {
  const [activeId, setActiveId] = React.useState<string>(ids[0] ?? '');

  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (records) => {
        const visible = records
          .filter((record) => record.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          );
        const first = visible[0];
        if (first) setActiveId(first.target.id);
      },
      // Fenêtre haute de l'écran : une section devient « courante » dès qu'elle
      // franchit le tiers supérieur, sous le décalage sticky (24px).
      { rootMargin: '-24px 0px -66% 0px', threshold: 0 },
    );

    for (const id of ids) {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [ids]);

  return [activeId, setActiveId] as const;
}

export function SupplierFaqScreen(): React.JSX.Element {
  const [query, setQuery] = React.useState('');

  // Filtre CLIENT-SIDE instantané (onChange, aucun submit) — insensible casse + accents.
  const needle = foldSearch(query);
  const filtered = React.useMemo(
    () => FAQ_ENTRIES.filter((entry) => matchesQuery(entry, needle)),
    [needle],
  );
  const filteredIds = React.useMemo(
    () => filtered.map((entry) => entry.id),
    [filtered],
  );

  // Le scrollspy suit le sous-ensemble FILTRÉ (ancres du sous-ensemble uniquement).
  const [activeId, setActiveId] = useScrollSpy(filteredIds);

  const handleNavClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      // BUG-FIX 2026-09-14 : surbrillance OPTIMISTE au clic — l'entrée choisie est mise en évidence
      // IMMÉDIATEMENT, sans attendre l'IntersectionObserver (qui ne se déclenche pas pour les entrées
      // de bas de liste ne pouvant jamais atteindre la bande active). Le scroll doux suit.
      setActiveId(id);
      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    },
    [setActiveId],
  );

  return (
    <section
      aria-labelledby="faq-title"
      className="mx-auto grid max-w-[1180px] grid-cols-1 gap-12 md:grid-cols-[300px_minmax(0,1fr)]"
    >
      {/* Colonne gauche — sommaire STICKY (titre + sous-titre + scrollspy + contact).
          <div> (pas <aside>) : le landmark de navigation est porté par le <nav> interne ;
          un <aside> imbriqué déclencherait landmark-complementary-is-top-level. */}
      <div className="flex flex-col gap-6 self-start md:sticky md:top-6">
        <header className="flex flex-col gap-1">
          <h1
            id="faq-title"
            className="font-serif text-2xl font-medium tracking-tight text-foreground"
          >
            Foire aux questions
          </h1>
          <p className="text-sm text-muted-foreground">
            Votre participation aux campagnes de référencement.
          </p>
        </header>

        <Input
          type="search"
          aria-label="Rechercher une question"
          placeholder="Rechercher une question…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="h-9"
        />

        <nav aria-label="Sommaire des questions">
          <ul className="flex flex-col gap-1 border-l border-border">
            {filtered.map((entry) => {
              const isActive = entry.id === activeId;
              return (
                <li key={entry.id}>
                  <a
                    href={`#${entry.id}`}
                    onClick={(event) => handleNavClick(event, entry.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={cn(
                      '-ml-px block border-l-2 py-1 pl-4 text-sm text-muted-foreground transition-colors hover:text-foreground',
                      isActive
                        ? 'border-foreground font-medium text-foreground'
                        : 'border-transparent',
                    )}
                  >
                    {entry.shortLabel}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex flex-col gap-1 rounded-md border border-border p-4">
          <p className="text-sm font-medium text-foreground">
            Pas de réponse ici&nbsp;?
          </p>
          <Link
            to="/echanges"
            className="text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Écrire à l’équipe JeKo
          </Link>
        </div>
      </div>

      {/* Colonne droite — lecture : les questions FILTRÉES, toutes OUVERTES (un <article> chacune). */}
      <div className="flex flex-col">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucune question ne correspond à votre recherche.
          </p>
        ) : (
          filtered.map((entry, index) => (
            <article
              key={entry.id}
              id={entry.id}
              aria-labelledby={`${entry.id}-title`}
              className={cn(
                'scroll-mt-24',
                index > 0 && 'mt-8 border-t border-border pt-8',
              )}
            >
              <h2
                id={`${entry.id}-title`}
                className="font-serif text-lg font-medium text-foreground"
              >
                {entry.question}
              </h2>
              <p className="mt-2 max-w-[640px] text-sm leading-[1.65] text-muted-foreground">
                {entry.answer}
              </p>
              <p className="mt-3 font-mono text-[10.5px] text-muted-foreground">
                {formatSource(entry.source)}
              </p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}
